import { MarkdownContent } from './MarkdownContent';
import { ToolCard } from './ToolCard';
import { ProposeCard } from './ProposeCard';

interface ToolCallRecord {
  id?: string;
  name?: string;
  input?: unknown;
  result?: unknown;
}

interface AttachmentRecord {
  externalId: string;
  publicUrl: string;
  width?: number | null;
  height?: number | null;
}

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallRecord[];
  attachments?: AttachmentRecord[];
  isStreaming?: boolean;
  durationMs?: number | null;
  tokenCount?: number | null;
  // Channel B (#613): context the propose card needs to fire an approve.
  sessionId?: string;
  approvedProposalIds?: Set<string>;
  onProposalApproved?: (id: string) => void;
}

export function MessageBubble({
  role,
  content,
  toolCalls,
  attachments,
  isStreaming,
  durationMs,
  tokenCount,
  sessionId,
  approvedProposalIds,
  onProposalApproved,
}: MessageBubbleProps) {
  if (role === 'user') {
    const hasAttachments = !!attachments && attachments.length > 0;
    return (
      <div className="flex flex-col items-end gap-1">
        {hasAttachments && (
          <div className="flex max-w-[85%] flex-wrap justify-end gap-1">
            {attachments!.map((att) => (
              <img
                key={att.externalId}
                src={att.publicUrl}
                alt=""
                loading="lazy"
                className="max-h-[200px] max-w-[200px] rounded-xl object-cover"
                style={
                  att.width && att.height
                    ? { aspectRatio: `${att.width}/${att.height}` }
                    : undefined
                }
              />
            ))}
          </div>
        )}
        {(content || !hasAttachments) && (
          <div className="max-w-[85%] rounded-2xl rounded-br-md bg-espresso px-4 py-2.5 text-sm text-ivory">
            {content}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[92%] space-y-1">
        {toolCalls?.map((tc, i) =>
          tc.name === 'propose_rowan_write' ? (
            <ProposeCard
              key={tc.id ?? i}
              proposalId={tc.id}
              sessionId={sessionId ?? ''}
              input={tc.result ?? tc.input}
              approved={tc.id ? approvedProposalIds?.has(tc.id) : false}
              onApproved={onProposalApproved}
            />
          ) : (
            <ToolCard
              key={tc.id ?? i}
              toolName={tc.name ?? 'tool'}
              result={tc.result ?? tc.input}
            />
          ),
        )}

        <div className="rounded-2xl rounded-bl-md border border-fog bg-white px-4 py-2.5 text-sm text-espresso">
          {content ? (
            <MarkdownContent content={content} />
          ) : isStreaming ? (
            <span className="text-bark/50">…</span>
          ) : null}
          {isStreaming && content && (
            <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-gold align-text-bottom" />
          )}
        </div>

        {!isStreaming && (durationMs || tokenCount) && (
          <div className="px-1 text-[11px] text-bark/60">
            {durationMs ? `${(durationMs / 1000).toFixed(1)}s` : ''}
            {tokenCount ? ` · ${tokenCount.toLocaleString()} tokens` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
